import { CssVarsThemeOptions } from "@mui/material/styles";
import { sharedNeutrals } from "@themes/tokens/shared";

type ThemeComponents = NonNullable<CssVarsThemeOptions["components"]>;

export const createBaseOverrides = (): ThemeComponents => ({
  MuiAvatar: {
    styleOverrides: {
      root: ({ theme }) => ({
        "& img": {
          objectFit: "contain"
        },
        backgroundColor: "inherit",
        boxShadow: theme.vars.appEffects.shadow.avatar,
        color: theme.vars.palette.text.secondary
      })
    }
  },
  MuiCheckbox: {
    styleOverrides: {
      // MUI's 9px (5px when small) is sized for a 48px touch target, which
      // leaves a checkbox floating in the middle of a box roughly twice the
      // size of the tick itself — very visible in a list, where the gap reads
      // as an indent rather than as padding. Halved: still comfortably
      // clickable, and the tick now sits where the row says it does.
      root: ({ theme }) => ({
        "&.MuiCheckbox-sizeSmall": {
          padding: 3
        },
        padding: 4,
        /**
         * A ticked box is `primary.main`, and in DARK mode that is the bottom
         * of the ramp — #032A25 on forest, which against `background.paper`
         * (#262A2E) measures 1.1:1. The box was being filled in a colour
         * indistinguishable from the panel behind it, so a ticked row and an
         * unticked one looked the same; the switch beside it still read only
         * because its white thumb and grey track carry it, not its colour.
         *
         * `primary.light` is the next step up the same ramp — #174D43, which
         * measures 1.5:1 there. Better, and it leaves every approved colour
         * alone, but still under the 3:1 a UI component needs; only step 400
         * (#5F8D73, 3.8:1) clears that, and it is on the same approved ramp if
         * this proves too faint in use. Light mode is untouched: there `main`
         * is the darker of the two and already reads against white.
         */
        ...theme.applyStyles("dark", {
          "&.Mui-checked, &.MuiCheckbox-indeterminate": {
            color: theme.vars.palette.primary.light
          }
        })
      })
    }
  },
  MuiChip: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: theme.shape.borderRadius
      })
    }
  },
  MuiCssBaseline: {
    styleOverrides: (theme) => ({
      body: {
        alignItems: "center",
        color: theme.vars.palette.text.primary,
        fontFamily: "Inter, sans-serif",
        margin: 0
      },
      html: {
        color: theme.vars.palette.text.primary,
        scrollbarColor: `${theme.vars.palette.primary.main} ${theme.vars.palette.surface.inset}`,
        scrollbarGutter: "stable",
        scrollbarWidth: "thin",
        scrollBehavior: "smooth"
      }
    })
  },
  MuiFormControlLabel: {
    styleOverrides: {
      label: ({ theme }) => ({
        fontSize: theme.typography.body2.fontSize
      })
    }
  },
  MuiListItemText: {
    styleOverrides: {
      primary: ({ theme }) => ({
        fontSize: theme.typography.body2.fontSize
      })
    }
  },
  MuiPaper: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: theme.shape.borderRadiusMd
      })
    }
  },
  MuiSkeleton: {
    defaultProps: {
      animation: "wave"
    },
    styleOverrides: {
      root: ({ ownerState, theme }) => {
        const variant = ownerState.variant ?? "rounded";
        const useMdRadius = variant === "rounded" || variant === "rectangular";
        return {
          "&.MuiSkeleton-wave::after": {
            background: `linear-gradient(90deg, transparent, rgba(24, 29, 39, 0.12), transparent)`,
            ...theme.applyStyles("dark", {
              background: `linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.14), transparent)`
            })
          },
          backgroundColor: theme.vars.palette.surface.inset,
          ...(useMdRadius && {
            borderRadius: theme.shape.borderRadiusMd
          })
        };
      }
    }
  },
  MuiSwitch: {
    styleOverrides: {
      root: ({ theme }) => ({
        "& .MuiSwitch-switchBase": {
          "&.Mui-checked": {
            "& + .MuiSwitch-track": {
              backgroundColor: `${theme.vars.ui.accent.data} !important`,
              opacity: 1
            },
            "&.Mui-disabled + .MuiSwitch-track": {
              opacity: 0.4
            }
          },

          "&.Mui-disabled .MuiSwitch-thumb": {
            backgroundColor: `${theme.vars.palette.grey[300]} !important`
          }
        },
        "& .MuiSwitch-thumb": {
          backgroundColor: sharedNeutrals.white
        },
        "& .MuiSwitch-track": {
          backgroundColor: `${theme.vars.palette.grey[400]} !important`,
          opacity: 1
        }
      })
    }
  }
});
